import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Grid,
} from '@mui/material';
import { Explore as ExploreIcon } from '@mui/icons-material';
import { getExploredPalaces, PalaceStatus } from '../../api/palace';

const PalaceExploreCard: React.FC = () => {
  const [palaces, setPalaces] = useState<PalaceStatus[]>([]);
  const [exploredCount, setExploredCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getExploredPalaces()
      .then(res => {
        if (res.success) {
          setPalaces(res.palaces);
          setExploredCount(res.explored_count);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <Card elevation={3}>
      <CardContent>
        {/* 標題列 */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar sx={{ bgcolor: '#667eea' }}>
            <ExploreIcon />
          </Avatar>
          <Box flex={1}>
            <Typography variant="h6" fontWeight="bold">
              命盤探索地圖
            </Typography>
            {!loading && (
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <LinearProgress
                  variant="determinate"
                  value={(exploredCount / 12) * 100}
                  sx={{ flex: 1, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {exploredCount} / 12 宮
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Grid container spacing={1}>
              {palaces.map(p => (
                <Grid item xs={4} sm={3} key={p.index}>
                  <Tooltip
                    title={
                      p.explored
                        ? `已探索 ${p.query_count} 次 — 點擊看推薦問題`
                        : `尚未探索 — 點擊試試：${p.recommended_question_text}`
                    }
                    arrow
                  >
                    <Box
                      onClick={() => navigate(`/questions/${p.recommended_question_id}`)}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                        borderRadius: 2,
                        cursor: 'pointer',
                        minHeight: 64,
                        bgcolor: p.explored ? '#ede7f6' : '#f5f5f5',
                        border: `2px solid ${p.explored ? '#9575cd' : '#e0e0e0'}`,
                        transition: 'transform 0.15s, box-shadow 0.15s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        textAlign="center"
                        color={p.explored ? 'secondary.dark' : 'text.secondary'}
                        sx={{ fontSize: '0.8rem' }}
                      >
                        {p.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        textAlign="center"
                        color="text.disabled"
                        sx={{ fontSize: '0.65rem', lineHeight: 1.2, mt: 0.3 }}
                      >
                        {p.topic}
                      </Typography>
                      {p.explored && (
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.65rem', color: 'secondary.main', mt: 0.3 }}
                        >
                          ×{p.query_count}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              textAlign="center"
              mt={1.5}
            >
              {exploredCount === 0
                ? '點擊任一宮位，前往推薦問題開始探索'
                : exploredCount < 12
                ? `還有 ${12 - exploredCount} 個宮位等待探索`
                : '已探索全部 12 個宮位！'}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PalaceExploreCard;
